// Shared render-capability tier (ASK-084 Part 0) — PURE (no THREE / no DOM) so the tier decision
// + profile table unit-test headlessly. The stage gathers capability FACTS (renderer.capabilities,
// UA, ?tier=) and calls resolveRenderTier once at init → stage.renderTier + stage.tierProfile.
//
// This is the general foundation: shadows is its FIRST consumer, but textures / IBL / LOD / post-fx
// / future GI read the SAME tier + profile. Adding a quality axis = add a knob to TIER_PROFILES,
// NOT a new per-feature detector.

export const TIERS = Object.freeze(["low", "mid", "high"]);   // extensible to "ultra"

// `envLodOnly` (ASK-142 R4, Client half): the IBL environment stops at the recipe's 1K LOD — no swap
// to the 2K. A PMREM'd env is a 256 px cube either way; the 2K cost a phone 6.5 MB on the wire and a
// ~100 ms main-thread RGBE decode (measured, LoAF) for a reflection it cannot resolve. Unity Quality
// Settings "Texture Quality" / Unreal per-platform texture LOD bias are the precedent.
// `rawPictureMaxSide` (ASK-142 §14.2): a product picture with NO cooked level (still cooking, an external
// stock cover) is resized off-thread to this long side before upload — the tier's texture limit applies
// to every texture, not only the cooked ones (Unity Quality "Texture Quality" is global). 0 = no cap.
// `drsSteps` (ASK-142 R7): the dynamic-resolution ladder — DPR caps the frame-time governor may step
// down through (and back up) to hold the frame. Its first rung is the tier's `pixelRatioCap`. A desktop
// `high` keeps a shorter ladder: its GPU is not the one that runs out.
// tier → quality knobs. The single source of truth consumers read. `shadowFilter` is VSM across
// the board (ASK-084 §1.3 / DECISION-VSM-ratified: VSM is the permanent ceiling; PCSS is a
// render-truth-gated future decision, not committed). `casters` = max shadow-casting lights.
export const TIER_PROFILES = Object.freeze({
	low:  Object.freeze({ shadowMapSize: 1024, shadowFilter: "VSM", shadowRadius: 4, shadowBlurSamples: 8,  casters: 1, pixelRatioCap: 2, drsSteps: Object.freeze([2, 1.5, 1.25]), shadowsEnabled: true, envLodOnly: true, rawPictureMaxSide: 960 }),
	mid:  Object.freeze({ shadowMapSize: 1024, shadowFilter: "VSM", shadowRadius: 6, shadowBlurSamples: 12, casters: 1, pixelRatioCap: 2, drsSteps: Object.freeze([2, 1.5, 1.25]), shadowsEnabled: true, envLodOnly: false, rawPictureMaxSide: 0 }),
	high: Object.freeze({ shadowMapSize: 4096,   // ASK-089: 8×6 room ortho r≈8.35m → 2048 gave 8.2mm texels; the spinner's ~5mm wire rods PCF-erased. 4096 → 4.1mm (rods resolve). Desktop-only cost (+48MB depth map); low/mid unchanged — tier-honest: their soft pools ground fixtures, rod detail is a high-tier read.
							 shadowFilter: "VSM", shadowRadius: 8, shadowBlurSamples: 16, casters: 1, pixelRatioCap: 2, drsSteps: Object.freeze([2, 1.5]), shadowsEnabled: true, envLodOnly: false, rawPictureMaxSide: 0 }),
});

export function getTierProfile(tier) {
	return TIER_PROFILES[tier] || TIER_PROFILES.low;
}

/** Coarse GPU class from an (optional) unmasked WebGL renderer string. Software rasterisers force
 * `low`; otherwise null → let maxTextureSize + mobile decide (no heavy benchmark, ASK-084 §0). */
export function classifyGpu(rendererString) {
	const s = String(rendererString || "").toLowerCase();
	if (!s) return null;
	if (/swiftshader|llvmpipe|software|basic render/.test(s)) return "software";
	if (/apple m\d|rtx|radeon rx|geforce|quadro|arc a\d/.test(s)) return "high";
	if (/adreno|mali|powervr|apple a\d/.test(s)) return "low";   // mobile GPUs → low base
	return null;
}

/** Resolve the render tier from capability facts. Deterministic; computed once. Facts:
 *   { override?, isWebGL2?, isMobile?, maxTextureSize?, gpuClass? }
 * Precedence: explicit ?tier= override > no-WebGL2/software floor > mobile floor > desktop by
 * GPU class / maxTextureSize. Mobile floors to `low` (the real mobile target); `high` is desktop. */
export function resolveRenderTier(facts = {}) {
	const o = String(facts.override || "").toLowerCase();
	if (TIERS.includes(o)) return o;                          // ?tier= wins (testing + render-truth)
	if (facts.isWebGL2 === false) return "low";               // WebGL1 → safe base
	if (facts.gpuClass === "software") return "low";
	if (facts.isMobile) return "low";                         // mobile floor
	const mts = Number(facts.maxTextureSize) || 0;
	if (facts.gpuClass === "high" || mts >= 16384) return "high";
	if (facts.gpuClass === "mid" || mts >= 8192) return "mid";
	return "low";
}

// ─── ASK-142 R7 — dynamic resolution: hold the frame time by lowering the DPR ──────────────────────────
//
// Every console/mobile title scales render resolution to hold its frame budget (Unreal's Dynamic
// Resolution, Unity's DRS); a web page's lever is the renderer's pixel ratio, and on a phone it is the
// big one — DPR 2 → 1.5 is 44 % fewer pixels, 2 → 1.25 is 61 %. The governor is a small state machine:
//
//   • it reads the REAL frame interval (rAF to rAF, which is what a thumb feels), each sample clamped
//     to `clampMs` so one 255 ms decode hitch cannot drop resolution — a CPU long task is not fixed by
//     drawing fewer pixels; only a SUSTAINED slow frame is;
//   • it judges whole windows of `windowFrames` frames (≈ 0.75 s at 60 fps): a window whose mean is over
//     `budgetMs` (20 ms — i.e. no longer holding 50 fps) steps DOWN one rung;
//   • a frame interval is quantised by vsync (a 60 Hz phone never reports less than 16.7 ms), so
//     head-room cannot be read off it. Climbing back is therefore a PROBE with back-off (Babylon's
//     SceneOptimizer, PlayCanvas's resolution governor): after `recoverWindows` consecutive windows at
//     or under `recoverMs`, step UP one rung; if the very next windows step it straight back down, the
//     wait before the next probe doubles (to `maxBackoff`× ≈ 36 s), so a device on the edge settles instead of
//     flickering between two resolutions;
//   • rungs are DPR caps: the effective DPR is min(device DPR, rung), and rungs that change nothing on
//     this device are collapsed — a DPR-1 desktop has one rung and the governor is inert.
// PURE: `stage.js` feeds it frame times and applies the DPR it returns.

export const DRS_DEFAULTS = Object.freeze({ budgetMs: 20, recoverMs: 17.5, windowFrames: 45, recoverWindows: 3, clampMs: 50, maxBackoff: 16, settleWindows: 2 });

/** The distinct effective DPRs for a ladder on a device, highest first. */
export function drsLevels(steps, deviceDpr) {
	const dev = Number(deviceDpr) > 0 ? Number(deviceDpr) : 1;
	const out = [];
	for (const s of steps && steps.length ? steps : [dev]) {
		const v = Math.min(dev, Number(s));
		if (v > 0 && !out.some((o) => Math.abs(o - v) < 1e-6)) out.push(v);
	}
	return out.sort((a, b) => b - a);
}

/** A dynamic-resolution governor. `push(frameMs)` returns the new DPR when it changes, else null. */
export function createDynamicResolution({ steps, deviceDpr, ...opts } = {}) {
	const o = { ...DRS_DEFAULTS, ...opts };
	const levels = drsLevels(steps, deviceDpr);
	let level = 0, n = 0, sum = 0, goodWindows = 0, backoff = 1, sinceClimb = Infinity, climbHeld = false, drops = 0, climbs = 0;
	const state = {
		get dpr() { return levels[level]; },
		get level() { return level; },
		get levels() { return levels.slice(); },
		get backoff() { return backoff; },
		get drops() { return drops; },
		get climbs() { return climbs; },
		/** Feed one frame interval (ms). Returns the new DPR if this frame closed a window that changed it. */
		push(frameMs) {
			if (!(frameMs > 0) || levels.length < 2) return null;
			sum += Math.min(frameMs, o.clampMs);
			if (++n < o.windowFrames) return null;
			const mean = sum / n;
			n = 0; sum = 0;
			sinceClimb++;
			if (mean > o.budgetMs) {
				goodWindows = 0;
				if (level >= levels.length - 1) return null;
				// A drop inside the settle period after a climb means the climb failed: back off.
				if (sinceClimb <= o.settleWindows) backoff = Math.min(o.maxBackoff, backoff * 2);
				climbHeld = false;
				level++; drops++;
				return levels[level];
			}
			if (mean <= o.recoverMs && level > 0) {
				if (++goodWindows >= o.recoverWindows * backoff) {
					goodWindows = 0; level--; climbs++; sinceClimb = 0; climbHeld = true;
					return levels[level];
				}
			} else if (mean > o.recoverMs) goodWindows = 0;
			// A climb that held through its settle period has proved itself: forgive the back-off.
			if (climbHeld && sinceClimb === o.settleWindows + 1) backoff = Math.max(1, backoff / 2);
			return null;
		},
		/** Start again from the top rung (a new scene; a changed device DPR wants a NEW governor instead). */
		reset() { level = 0; n = 0; sum = 0; goodWindows = 0; sinceClimb = Infinity; climbHeld = false; },
	};
	return state;
}
